import { Pencil, Trash2 } from "lucide-react";
import React from "react";
import { Badge } from "../../../../../components/ui/common/badge";
import { Button } from "../../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/ui/common/tooltip";
import { recordToFormValues } from "../utils/wasteRecords.utils";
import { MgmtOption, WasteFormValues, WasteRecord } from "../types/wasteRecords.types";
import WasteRecordForm from "./WasteRecordForm";

interface WasteRecordRowProps {
  rec: WasteRecord;
  idx: number;
  ingredientOptions: string[];
  mgmtOptions: MgmtOption[];
  mgmtLabelMap: Record<string, string>;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (values: WasteFormValues) => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}

/** One waste record: a read strip, or (while editing) the inline edit form. */
const WasteRecordRow: React.FC<WasteRecordRowProps> = ({
  rec,
  idx,
  ingredientOptions,
  mgmtOptions,
  mgmtLabelMap,
  isEditing,
  onEdit,
  onSave,
  onCancelEdit,
  onDelete
}) => {
  if (isEditing) {
    return (
      <div className="tw-border tw-border-primary/30 tw-rounded-md tw-p-1 tw-bg-primary/5">
        <WasteRecordForm
          defaultValues={recordToFormValues(rec)}
          onSave={onSave}
          onCancel={onCancelEdit}
          onDelete={onDelete}
          ingredientOptions={ingredientOptions}
          mgmtOptions={mgmtOptions}
          isEdit
          title={`Waste ${idx + 1}${rec.streamName ? ` — ${rec.streamName}` : ""}`}
        />
      </div>
    );
  }

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-2 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50">
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
        <span className="tw-font-semibold tw-text-muted-foreground tw-w-5">{idx + 1}</span>
        <span className="tw-font-medium tw-text-foreground">{rec.streamName || "—"}</span>
        {rec.managementType && (
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {mgmtLabelMap[rec.managementType] ?? rec.managementType}
          </Badge>
        )}
        <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
          {rec.distribution || 0} {rec.distributionUnit}
        </span>
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
              onClick={onEdit}
            >
              <Pencil className="tw-h-3 tw-w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="tw-text-xs">Edit</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Button variant="ghost" size="icon" className="tw-h-7 tw-w-7 tw-text-destructive" onClick={onDelete}>
        <Trash2 className="tw-h-3 tw-w-3" />
      </Button>
    </div>
  );
};

export default WasteRecordRow;
